import PropTypes from 'prop-types';

import { Chart } from 'react-chartjs-2';
import { useRef } from 'react';
import ExportDropdown from './ExportDropdown';
import { numberFormat } from '@/utils/unitUtils';

const distributionOptions = {
  emissions: {
    backgroundColor: '#D0E2FF',
    borderColor: '#A2C5FF',
    kdeColor: '#1D6FAD',
  },
  levelized_cost: {
    backgroundColor: '#FFBC71',
    borderColor: '#E8750F',
    kdeColor: '#B84B00',
  },
};

const buildOptions = (labels, xAxisLabel, scalar) => ({
  plugins: {
    legend: {
      display: false,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      title: { display: true, text: xAxisLabel },
      ticks: {
        callback: (_value, index, _ticks) => {
          return index % 4 == 0 ? numberFormat(labels[index] * scalar) : null;
        },
      },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Probability' },
    },
  },
});

const buildData = (data, labels, distribution, scalar, kde) => {
  const { backgroundColor, borderColor, kdeColor } = distributionOptions[distribution];
  const adjustedLabels = labels.map(label => `${numberFormat(label * scalar)}`);

  const datasets = [
    {
      type: 'bar',
      backgroundColor,
      data,
      borderColor,
      borderWidth: 1,
      barPercentage: 1,
      categoryPercentage: 1,
      order: 1,
    },
  ];

  if (kde) {
    datasets.push({
      type: 'line',
      data: kde,
      borderColor: kdeColor,
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.4,
      fill: false,
      order: 0,
    });
  }

  return {
    labels: adjustedLabels,
    datasets,
  };
};

const MCChart = ({ data, labels, distribution, xAxisLabel, scalar, kde }) => {
  const chartScalar = distribution === 'levelized_cost' ? scalar : 1;
  const options = buildOptions(labels, xAxisLabel, chartScalar);
  const barData = buildData(data, labels, distribution, chartScalar, kde);
  const ref = useRef();

  return (
    <div className="min-h-88 relative w-full pt-6">
      <ExportDropdown forwardedRef={ref} data={barData} />
      <Chart ref={ref} data={barData} options={options} />
    </div>
  );
};

MCChart.propTypes = {
  data: PropTypes.array,
  labels: PropTypes.arrayOf(PropTypes.number),
  distribution: PropTypes.string,
  xAxisLabel: PropTypes.string,
  scalar: PropTypes.number,
  kde: PropTypes.arrayOf(PropTypes.number),
};

export default MCChart;
